import { Popover } from '@mui/material';
import { forwardRef, Ref, RefObject, useImperativeHandle, useMemo, useRef, useState } from 'react';

import {
  EditCalendarEventForm,
  EditCalendarEventFormProps,
  EditCalendarEventFormRef,
  EditCalendarEventFormValues,
} from './EditCalendarEventForm';

import { Dialog, DialogRef } from '@/components/common/Dialog';
import { Button, CloseButton } from '@/components/ds';
import {
  CalendarEntryType,
  CalendarEvent,
  CreateCalendarEvent,
  GenericCalendarEntry,
  UpdateCalendarEvent,
} from '@/models/CalendarEntry';
import { useTranslationPrefix } from '@/translations/i18n';

export type EditCalendarEventDialogRef = DialogRef;

export interface EditCalendarEventDialogProps<T extends EditCalendarEventFormValues> {
  /**
   * The selected calendar entry. Can be a `CalendarEvent` or not. Should not be provided when creating a new entry.
   */
  selectedCalendarEntry: T extends CreateCalendarEvent
    ? undefined
    : T extends UpdateCalendarEvent
    ? GenericCalendarEntry | null
    : never;
  /**
   * Callback fired when the form is submitted.
   */
  onSubmit: T extends CreateCalendarEvent
    ? (values: CreateCalendarEvent) => void
    : T extends UpdateCalendarEvent
    ? (id: string, values: UpdateCalendarEvent) => void
    : never;
  /**
   * Whether the dialog is loading or not. If true, the dialog will be disabled and the submit button will be loading.
   */
  isLoading?: boolean;
  /** Currently selected color swatch. */
  selectedColor?: string;
  /** Called when the user picks a color swatch. */
  onColorChange?: (color: string) => void;
  /** When given, the form drops down from this element as a panel instead of taking over the screen as a dialog */
  anchorRef?: RefObject<HTMLElement | null>;
}

const EditCalendarEventDialogComponent = <T extends EditCalendarEventFormValues>(
  {
    selectedCalendarEntry,
    onSubmit,
    isLoading,
    selectedColor,
    onColorChange,
    anchorRef,
  }: EditCalendarEventDialogProps<T>,
  ref: Ref<EditCalendarEventDialogRef>,
) => {
  const dialogRef = useRef<DialogRef>(null);
  const formRef = useRef<EditCalendarEventFormRef<T>>(null);
  const selectedCalendarEvent = useMemo(
    () =>
      (selectedCalendarEntry
        ? selectedCalendarEntry.type === CalendarEntryType.Event
          ? (selectedCalendarEntry as CalendarEvent)
          : null
        : undefined) as EditCalendarEventFormProps<T>['event'],
    [selectedCalendarEntry],
  );
  const isNewEvent = selectedCalendarEvent === undefined;
  const t = useTranslationPrefix(
    `home.calendar.events.${isNewEvent ? 'create_dialog' : 'edit_dialog'}`,
  );

  const onFormSubmit = (values: T) => {
    if (isNewEvent) {
      // Create
      (onSubmit as EditCalendarEventDialogProps<CreateCalendarEvent>['onSubmit'])(
        values as CreateCalendarEvent,
      );
    } else {
      // Update
      selectedCalendarEvent &&
        (onSubmit as EditCalendarEventDialogProps<UpdateCalendarEvent>['onSubmit'])(
          selectedCalendarEvent.id,
          values as UpdateCalendarEvent,
        );
    }
  };

  const resetForm = () => {
    formRef.current?.resetForm();
  };

  const [popOpen, setPopOpen] = useState(false);
  useImperativeHandle(ref, () =>
    anchorRef
      ? {
          open: () => setPopOpen(true),
          close: () => {
            setPopOpen(false);
            resetForm();
          },
        }
      : // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        dialogRef.current!,
  );

  if (anchorRef) {
    // the drop-down form: a solid panel hanging from its button (Tradehaus .tx-pop), not a screen takeover
    return (
      <Popover
        open={popOpen}
        anchorEl={anchorRef.current}
        onClose={() => {
          if (isLoading) return;
          setPopOpen(false);
          resetForm();
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              mt: '8px',
              width: { xs: 'calc(100vw - 20px)', sm: 420 },
              maxWidth: 'calc(100vw - 20px)',
              boxSizing: 'border-box',
              p: '20px',
              backgroundColor: 'var(--tx-dialog)',
              backgroundImage: 'var(--tx-surface-light)',
              backdropFilter: 'var(--tx-blur-panel)',
              WebkitBackdropFilter: 'var(--tx-blur-panel)',
              borderRadius: '20px',
              boxShadow: '0 16px 48px rgba(0, 0, 0, 0.4)',
              fontFamily: "'Manrope', sans-serif",
            },
          },
        }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '-4px -4px 16px 0' }}>
          <span style={{ fontSize: 17, fontWeight: 400, letterSpacing: '-0.015em', color: 'var(--tx-text)' }}>{t('title')}</span>
          <CloseButton
            size="sm"
            onClose={() => {
              if (isLoading) return;
              setPopOpen(false);
              resetForm();
            }}
          />
        </div>
        <EditCalendarEventForm
          ref={formRef}
          event={selectedCalendarEvent}
          onSubmit={onFormSubmit}
          selectedColor={selectedColor}
          onColorChange={onColorChange}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
          <Button variant="ghost" disabled={isLoading} onClick={() => setPopOpen(false)}>
            {t('cancel')}
          </Button>
          <Button variant="primary" disabled={isLoading} onClick={() => formRef.current?.submitForm()}>
            {isLoading ? 'Saving…' : t('submit')}
          </Button>
        </div>
      </Popover>
    );
  }

  return (
    <Dialog
      ref={dialogRef}
      title={t('title')}
      actions={
        // the house dialog foot: a quiet cancel, and the one lime action
        <>
          <Button variant="ghost" disabled={isLoading} onClick={() => dialogRef.current?.close()}>
            {t('cancel')}
          </Button>
          <Button variant="primary" disabled={isLoading} onClick={() => formRef.current?.submitForm()}>
            {isLoading ? 'Saving…' : t('submit')}
          </Button>
        </>
      }
      onClose={resetForm}
      disableClose={isLoading}>
      <EditCalendarEventForm
        ref={formRef}
        event={selectedCalendarEvent}
        onSubmit={onFormSubmit}
        selectedColor={selectedColor}
        onColorChange={onColorChange}
      />
    </Dialog>
  );
};

export const EditCalendarEventDialog = forwardRef(EditCalendarEventDialogComponent) as <
  T extends EditCalendarEventFormValues,
>(
  props: EditCalendarEventDialogProps<T> & { ref?: Ref<EditCalendarEventDialogRef> },
) => ReturnType<typeof EditCalendarEventDialogComponent<T>>;
